// Решетката на работния лист „Доставка": плоски клетки (id
// `dwx-{продукт}-{колона}`, стрелкова навигация от страницата), „тежките"
// клетки Разфасовка/Срок/Склад са общият PickCell, колона ДДС, ⚑ (проблем) и
// ✕ на реда, редовете на обобщените стокови (фактура над стокови — само за
// четене), празното състояние и лепнатият в дъното ред „Общо". Под
// десктоп ширина редовете са карти (DeliveryLineCards) със СЪЩИТЕ id-та на
// клетките. Логиката на редовете е в `useDeliveryRowsActions`, малките
// клетки (заглавие, обвивки, видът на затворената „тежка" клетка) — в
// `DeliveryWorksheetCells`.
import { DatePicker, Select } from "antd";
import { CloseOutlined, FlagOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type {
  Delivery,
  LastPrice,
  Packaging,
  Product,
  StockBalance,
  Warehouse,
} from "../../../../api/inventory";
import { DeliveryLineCards } from "./DeliveryLineCards";
import { DeliveryLinkedNoteCards, DeliveryLinkedNoteRows } from "./DeliveryLinkedNoteRows";
import { DeliveryTotalsRow } from "./DeliveryTotalsRow";
import { DeliveryEmptyState } from "./DeliveryEmptyState";
import { formatSignedDelta, type TotalColumn } from "./deliveryTotalAdjust";
import type { DeliveryIssueField } from "./deliveryIssues";
import { PickCell } from "../../components/PickCell";
import { NegativeStockMarker } from "../../components/NegativeStockMarker";
import { TruncatedText } from "../../components/list/TruncatedText";
import {
  SHEET_CONTROL_HEIGHT,
  SHEET_HEADER_ROW,
  SHEET_INPUT,
  SHEET_META_TEXT,
  SHEET_NAME_CELL,
  SHEET_NAME_TEXT,
  SHEET_ROW,
} from "../../components/list/density";
import { CellBox, PickBox, Th } from "./DeliveryWorksheetCells";
import {
  FLAT_CELL_CLASS,
  FLAT_CELL_ERROR_CLASS,
  LINE_GRID_CLASS,
  LINE_GRID_MIN_WIDTH_CLASS,
  formatUnitPrice,
  formatQtyInput,
  isRowTouched,
  parseDecimal,
  pickCellClassOf,
  type DeliveryRowDraft,
} from "./deliveryWorksheetShared";

export type NumericCol = "qty" | "price" | "net" | "gross";

type Props = {
  addedProducts: Product[];
  rowOf: (productId: number) => DeliveryRowDraft;
  balanceByProduct: Map<number, StockBalance>;
  lastPriceByProduct: Map<number, LastPrice>;
  packagingById: Map<number, Packaging>;
  warehouses: Warehouse[];
  decimalsOf: (productId: number) => number;
  // id на клетка `dwx-{продукт}-{колона}` (от useGridNavigation)
  cellId: (productId: number, col: number) => string;
  isDesktop: boolean;
  onCellChange: (
    productId: number,
    col: NumericCol | "batch",
    value: string,
  ) => void;
  onCellBlur: (productId: number, col: NumericCol) => void;
  onPackagingChange: (productId: number, packagingId: number | null) => void;
  onExpiryChange: (productId: number, expiry: string) => void;
  onRowWarehouseChange: (productId: number, warehouseId: number) => void;
  // ←/→ от отворено падащо меню → съседната клетка
  onSideExit: (productId: number, col: number, step: -1 | 1) => void;
  onRemoveRow: (productId: number) => void;
  // ⚑ — маркиране на проблем с продукта
  onFlag: (productId: number) => void;
  // ДДС се начислява (регистрирана фирма + доставчик с ДДС №)
  vatApplies: boolean;
  // сумите за реда „Общо" в дъното на решетката (със стоковите на фактурата)
  totalNet: number;
  totalGross: number;
  // фактура над стокови: техните редове под собствените (само за четене)
  linkedNotes: Delivery[];
  productById: Map<number, Product>;
  // „Коригирай общото" — въведеното в „Общо" нето/бруто
  onAdjustTotal: (column: TotalColumn, target: number, current: number) => void;
  // Червената рамка: полето е пречка за записа (`deliveryIssues` през useDeliverySave)
  isCellInvalid: (productId: number, field: DeliveryIssueField) => boolean;
};

export const DeliveryWorksheetTable = ({
  addedProducts,
  rowOf,
  balanceByProduct,
  lastPriceByProduct,
  packagingById,
  warehouses,
  decimalsOf,
  cellId,
  isDesktop,
  onCellChange,
  onCellBlur,
  onPackagingChange,
  onExpiryChange,
  onRowWarehouseChange,
  onSideExit,
  onRemoveRow,
  onFlag,
  vatApplies,
  totalNet,
  totalGross,
  linkedNotes,
  productById,
  onAdjustTotal,
  isCellInvalid,
}: Props) => {
  const { t } = useTranslation();
  const noteLineCount = linkedNotes.reduce((sum, note) => sum + note.lines.length, 0);
  const hasRows = addedProducts.length > 0 || noteLineCount > 0;

  // Под десктоп ширина — карти (същите id-та на клетките)
  if (!isDesktop)
    return (
      <>
        {/* празното състояние на картите — само без редове от стокови */}
        {(addedProducts.length > 0 || noteLineCount === 0) && (
          <DeliveryLineCards
            addedProducts={addedProducts}
            rowOf={rowOf}
            balanceByProduct={balanceByProduct}
            packagingById={packagingById}
            warehouses={warehouses}
            decimalsOf={decimalsOf}
            cellId={cellId}
            vatApplies={vatApplies}
            onCellChange={onCellChange}
            onCellBlur={onCellBlur}
            onPackagingChange={onPackagingChange}
            onExpiryChange={onExpiryChange}
            onRowWarehouseChange={onRowWarehouseChange}
            onRemoveRow={onRemoveRow}
            onFlag={onFlag}
            isCellInvalid={isCellInvalid}
          />
        )}
        <DeliveryLinkedNoteCards notes={linkedNotes} productById={productById} decimalsOf={decimalsOf} />
      </>
    );

  // Клетка с число: value/placeholder/стил по колоната (както оригиналните
  // input-и на монолитната страница — качество/цена/нето/бруто)
  const numericInput = (
    product: Product,
    col: NumericCol,
    colIndex: number,
    value: string,
    extraClass: string,
    title?: string,
    invalid = false,
  ) => (
    <CellBox>
      <input
        id={cellId(product.id, colIndex)}
        title={title}
        aria-invalid={invalid || undefined}
        className={`${FLAT_CELL_CLASS} ${SHEET_INPUT} px-[9px] text-right font-[ui-monospace,Menlo,monospace] text-[13px] ${extraClass} ${
          invalid ? FLAT_CELL_ERROR_CLASS : ""
        }`}
        value={value}
        placeholder={col === "qty" ? "0" : "0.00"}
        inputMode="decimal"
        data-rk={product.id}
        data-c={colIndex}
        onChange={(e) => onCellChange(product.id, col, e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => onCellBlur(product.id, col)}
      />
    </CellBox>
  );

  return (
    <div className={`${LINE_GRID_MIN_WIDTH_CLASS} flex min-h-full flex-col`}>
      {/* Заглавен ред (sticky) */}
      <div
        className={`${LINE_GRID_CLASS} ${SHEET_HEADER_ROW} sticky top-0 z-[5] border-b border-shade-3 bg-fill`}
      >
        <Th center>#</Th>
        <Th>{t("inventoryPage.colProductName")}</Th>
        <Th>{t("inventoryPage.colPackaging")}</Th>
        <Th>{t("inventoryPage.colQty")}</Th>
        <Th>{t("inventoryPage.colUnitPrice")}</Th>
        <Th right>{t("inventoryPage.colVat")}</Th>
        <Th right>{t("inventoryPage.colNet")}</Th>
        <Th right>{t("inventoryPage.colGross")}</Th>
        <Th>{t("inventoryPage.colBatch")}</Th>
        <Th>{t("inventoryPage.colExpiry")}</Th>
        <Th>{t("inventoryPage.colWarehouse")}</Th>
        <div />
      </div>

      {addedProducts.map((product, rowIndex) => {
        const row = rowOf(product.id);
        const filled = parseDecimal(row.qty) > 0;
        const batchError = isCellInvalid(product.id, "batch");
        const expiryError = isCellInvalid(product.id, "expiry");
        const balance = balanceByProduct.get(product.id);
        const lastPrice = lastPriceByProduct.get(product.id);
        const packagingOptions = [
          { value: 0, label: product.unit ?? "—" },
          ...(product.packagingIds ?? [])
            .map((id) => packagingById.get(id))
            .filter(Boolean)
            .map((pkg) => ({ value: pkg!.id, label: pkg!.name })),
        ];
        return (
          <div
            key={product.id}
            className={`${LINE_GRID_CLASS} ${SHEET_ROW} items-stretch border-b border-shade-2 ${
              filled ? "bg-panel" : "bg-surface"
            }`}
          >
            {/* # */}
            <div className="flex items-center justify-center border-r border-shade-2 bg-fill font-[ui-monospace,Menlo,monospace] text-[11px] font-semibold leading-[normal] text-muted">
              {rowIndex + 1}
            </div>
            {/* ПРОДУКТ (статичен: име + наличност/посл. цена) */}
            <div className={`${SHEET_NAME_CELL} border-r border-shade-2`}>
              <TruncatedText text={product.name} className={SHEET_NAME_TEXT} />
              <span className={`${SHEET_META_TEXT} text-muted`}>
                {t("inventoryPage.productOptStock")}:{" "}
                {formatQtyInput(
                  Math.max(0, balance?.quantity ?? 0),
                  decimalsOf(product.id),
                )}
                {product.unit ? ` ${product.unit}` : ""}
                {/* минусът от продажби — до клампнатата нула */}
                <NegativeStockMarker
                  quantity={balance?.quantity ?? 0}
                  decimals={decimalsOf(product.id)}
                  className="ml-1 font-semibold"
                />
                {lastPrice != null
                  ? ` · ${t("inventoryPage.wsLastPrice")} ${formatUnitPrice(lastPrice.unitPrice)}`
                  : ""}
              </span>
              {row.problem && (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => onFlag(product.id)}
                  className="mt-0.5 self-start truncate rounded-md border-none bg-danger-tint px-2 py-0.5 text-left text-xs leading-[normal] text-danger-ink"
                >
                  ⚑ {row.problem}
                </button>
              )}
              {!!row.totalAdjust && (
                <span className={`${SHEET_META_TEXT} text-brand-deep-ink`}>
                  {t("inventoryPage.tadjRowNote", { delta: formatSignedDelta(row.totalAdjust) })}
                </span>
              )}
            </div>
            {/* РАЗФАС. (c=0) */}
            <PickBox>
              <PickCell
                id={cellId(product.id, 0)}
                rowKey={product.id}
                editorHeight={SHEET_CONTROL_HEIGHT}
                col={0}
                className={pickCellClassOf(!isRowTouched(row))}
                onSideExit={(step) => onSideExit(product.id, 0, step)}
                renderEditor={(close) => (
                  <Select
                    autoFocus
                    defaultOpen
                    value={row.packagingId ?? 0}
                    onChange={(v) => {
                      onPackagingChange(
                        product.id,
                        v === 0 ? null : (v as number),
                      );
                      close();
                    }}
                    onBlur={() => close(false)}
                    options={packagingOptions}
                    className="w-full"
                  />
                )}
              >
                <span className="truncate">
                  {row.packagingId
                    ? (packagingById.get(row.packagingId)?.name ?? "—")
                    : (product.unit ?? "—")}
                </span>
              </PickCell>
            </PickBox>
            {/* КОЛИЧЕСТВО (c=1) */}
            {numericInput(
              product,
              "qty",
              1,
              row.qty,
              "font-semibold text-ink",
              undefined,
              isCellInvalid(product.id, "qty"),
            )}
            {/* ЕД. ЦЕНА (c=2) — авто-цената е сива и в курсив; изведената от
                въведена сума (нето/бруто) е сива, с подсказка */}
            {numericInput(
              product,
              "price",
              2,
              row.price,
              row.priceAuto
                ? "italic text-muted"
                : row.typedSum
                  ? "text-muted"
                  : "text-ink",
              row.typedSum ? t("inventoryPage.upPriceFromSum") : undefined,
              isCellInvalid(product.id, "price"),
            )}
            {/* ДДС (само за четене — от продукта и доставчика) */}
            <div className="flex items-center justify-end border-r border-shade-2 px-2.5 text-[12.5px] text-body">
              {vatApplies && product.vatRate != null ? `${product.vatRate}%` : "—"}
            </div>
            {/* НЕТО (c=3) */}
            {numericInput(
              product,
              "net",
              3,
              row.net,
              `font-semibold ${
                parseDecimal(row.net) > 0 ? "text-ink" : "text-ghost"
              }`,
            )}
            {/* БРУТО (c=4) */}
            {numericInput(
              product,
              "gross",
              4,
              row.gross,
              `font-semibold ${
                parseDecimal(row.gross) > 0
                  ? "text-ink"
                  : "text-ghost"
              }`,
            )}
            {/* ПАРТИДА (c=5) */}
            <CellBox>
              <input
                id={cellId(product.id, 5)}
                className={`${FLAT_CELL_CLASS} ${SHEET_INPUT} px-[9px] text-center font-[ui-monospace,Menlo,monospace] text-[12.5px] text-body ${
                  batchError ? FLAT_CELL_ERROR_CLASS : ""
                }`}
                value={row.batch}
                placeholder="—"
                data-rk={product.id}
                data-c={5}
                onChange={(e) =>
                  onCellChange(product.id, "batch", e.target.value)
                }
                onFocus={(e) => e.target.select()}
              />
            </CellBox>
            {/* СРОК (c=6) — в DatePicker ←/→ движат курсора, без onSideExit */}
            <PickBox>
              <PickCell
                id={cellId(product.id, 6)}
                rowKey={product.id}
                editorHeight={SHEET_CONTROL_HEIGHT}
                col={6}
                className={`${pickCellClassOf(!row.expiry)} ${
                  expiryError ? FLAT_CELL_ERROR_CLASS : ""
                }`}
                renderEditor={(close) => (
                  <DatePicker
                    autoFocus
                    defaultOpen
                    value={row.expiry ? dayjs(row.expiry) : null}
                    format="DD.MM.YYYY"
                    onChange={(d) => {
                      onExpiryChange(
                        product.id,
                        d ? d.format("YYYY-MM-DD") : "",
                      );
                      close();
                    }}
                    onOpenChange={(open) => {
                      if (!open) close();
                    }}
                    className="w-full"
                  />
                )}
              >
                <span className="truncate">{row.expiry ? dayjs(row.expiry).format("DD.MM.YYYY") : "—"}</span>
              </PickCell>
            </PickBox>
            {/* СКЛАД (c=7) */}
            <PickBox>
              <PickCell
                id={cellId(product.id, 7)}
                rowKey={product.id}
                editorHeight={SHEET_CONTROL_HEIGHT}
                col={7}
                className={`${pickCellClassOf(!isRowTouched(row))} ${
                  isCellInvalid(product.id, "warehouse") ? FLAT_CELL_ERROR_CLASS : ""
                }`}
                onSideExit={(step) => onSideExit(product.id, 7, step)}
                renderEditor={(close) => (
                  <Select
                    autoFocus
                    defaultOpen
                    value={row.warehouseId ?? undefined}
                    onChange={(v) => {
                      onRowWarehouseChange(product.id, v as number);
                      close();
                    }}
                    onBlur={() => close(false)}
                    options={warehouses.map((w) => ({
                      value: w.id,
                      label: w.name,
                    }))}
                    className="w-full"
                  />
                )}
              >
                <span className="truncate">
                  {warehouses.find((w) => w.id === row.warehouseId)?.name ?? "—"}
                </span>
              </PickCell>
            </PickBox>
            {/* ⚑ проблем · ✕ премахване (извън Tab реда — клавиатурата е по клетките) */}
            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                onClick={() => onFlag(product.id)}
                title={t("inventoryPage.problemFlagHint")}
                aria-label={t("inventoryPage.problemFlagHint")}
                tabIndex={-1}
                className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-[13px] hover:bg-shade-1 ${
                  row.problem ? "text-danger-ink" : "text-ghost hover:text-ink"
                }`}
              >
                <FlagOutlined />
              </button>
              <button
                type="button"
                onClick={() => onRemoveRow(product.id)}
                title={t("inventoryPage.wsClearRowHint")}
                aria-label={t("inventoryPage.wsClearRowHint")}
                tabIndex={-1}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-[13px] text-ghost hover:bg-shade-1 hover:text-danger-ink"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
        );
      })}

      <DeliveryLinkedNoteRows notes={linkedNotes} productById={productById} decimalsOf={decimalsOf} />

      {!hasRows && <DeliveryEmptyState isDesktop={isDesktop} />}

      {hasRows && (
        <DeliveryTotalsRow
          count={addedProducts.length + noteLineCount}
          totalNet={totalNet}
          totalGross={totalGross}
          vatApplies={vatApplies}
          onAdjustTotal={onAdjustTotal}
        />
      )}
    </div>
  );
};

export default DeliveryWorksheetTable;
